// TelcoTrack — čisté funkce pro ECharts „globální KPI v čase“ (sdílené dashboard + polygon dashboard).
// Y osa = % splnění (čitatel/jmenovatel); absolutní ks/m zůstávají v tooltipu.

import type { BarSeriesOption, EChartsOption, LineSeriesOption } from 'echarts';
import { echartsEntranceMotion } from './stat-motion';
import { MANAGER_KPI_CARD_LABELS, type ManagerKpiCardId } from '@core/shared-bridge';
import type {
  ManagerDashboardTimeseriesPoint,
  ManagerDashboardTimeseriesResult,
} from '../../core/models/object.model';

/** Barvy řad KPI grafu — stejné i na snapshot kartách nad grafem. */
export const MANAGER_KPI_SERIES_COLORS = {
  routesR100: '#0891b2',
  routesMet: '#0d9488',
  routesUr: '#ea580c',
  routes: '#0891b2',
  objekt: '#ca8a04',
  rozvadec: '#16a34a',
  rozvadecPostaveny: '#0d9488',
  rozvadecZafouknuto: '#0284c7',
  vr: '#9333ea',
  babovka: '#c026d3',
  zafouknuto: '#e11d48',
  zafoukBabovka: '#be123c',
  zafoukPripojenaBudova: '#f43f5e',
  /** Hub headline Záfuk (součet budov + bábovek) — stejná barva jako připojovaná budova. */
  zafukBudovy: '#f43f5e',
  zafukOkDelka: '#0e7490',
  zafukSvazekDelka: '#7c3aed',
  hpPocet: '#059669',
  akvizicePocet: '#d97706',
  matrixSpoj: '#2563eb',
  zruseneBudovy: '#78716c',
  digitalniCesty: '#65a30d',
  svarObjekt: '#db2777',
  hotoveBudovy: '#15803d',
} as const;

/** Legenda / tooltip — popisky karet z `@cubby/shared` manager-kpi-categories. */
export const MANAGER_KPI_SERIES_NAMES = {
  ...MANAGER_KPI_CARD_LABELS,
  routes: 'Vykopaná délka tras',
  zafukBudovy: 'Záfuk budov a bábovek',
} as const;

export function escapeHtmlForChartTooltip(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

export function formatTimeseriesBucketLabel(
  bucketIso: string,
  bucket: ManagerDashboardTimeseriesResult['bucket'],
): string {
  const start = new Date(bucketIso);
  if (Number.isNaN(start.getTime())) return bucketIso;
  const fmt = (d: Date) =>
    d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  if (bucket === 'day') return fmt(start);
  if (bucket === 'week') {
    const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + 6));
    return `${fmt(start)} – ${fmt(end)}`;
  }
  const y = start.getUTCFullYear();
  const m = start.getUTCMonth();
  const endMonth = new Date(Date.UTC(y, m + 1, 0));
  return `${fmt(start)} – ${fmt(endMonth)}`;
}

/** Splněnost 0–100 %; bez jmenovatele = 0 %. */
export function kpiFractionPercent(numerator: number, denominator: number): number {
  const den = Math.max(0, Number(denominator) || 0);
  if (den <= 0) return 0;
  const num = Math.max(0, Number(numerator) || 0);
  return Math.min(100, (num / den) * 100);
}

type BarDatum = {
  value: number;
  itemStyle: Record<string, unknown>;
  label: Record<string, unknown>;
};

type StackedPercentSeries = { bottom: BarDatum[]; top: BarDatum[] };

function fmtInt(v: number): string {
  return `${Math.round(Number(v) || 0).toLocaleString('cs-CZ')}`;
}

function fmtMPart(v: number): string {
  return Number(v).toLocaleString('cs-CZ', { maximumFractionDigits: Math.abs(v) >= 1000 ? 0 : 1 });
}

function formatPctLabel(pct: number): string {
  return `${pct.toFixed(pct >= 10 || pct === 0 ? 0 : 1)}%`;
}

/** Skládané % sloupce: barevný čitatel + šedý zbytek do 100 % (stejná výška všech řad). */
export function buildKpiStackedBarSeriesData(
  pts: ManagerDashboardTimeseriesPoint[],
  text: string,
  dark: boolean,
  split: string,
) {
  const fracRoutesR100 = (i: number): string => {
    const r = pts[i]?.metricsSnapshot?.routes?.r100;
    if (!r) return '—/—';
    return `${fmtMPart(r.excavatedLengthM)}/${fmtMPart(r.plannedLengthM)} m`;
  };
  const fracRoutesMet = (i: number): string => {
    const r = pts[i]?.metricsSnapshot?.routes?.met;
    if (!r) return '—/—';
    return `${fmtMPart(r.excavatedLengthM)}/${fmtMPart(r.plannedLengthM)} m`;
  };
  const fracRoutesUr = (i: number): string => {
    const r = pts[i]?.metricsSnapshot?.routes?.ur;
    if (!r) return '—/—';
    return `${fmtMPart(r.excavatedLengthM)}/${fmtMPart(r.plannedLengthM)} m`;
  };
  const fracDrilled = (i: number): string => {
    const o = pts[i]?.metricsSnapshot?.objekt;
    if (!o) return '—/—';
    return `${fmtInt(o.drilledCount)}/${fmtInt(o.totalCount)} ks`;
  };
  const fracRoz = (i: number): string => {
    const z = pts[i]?.metricsSnapshot?.rozvadec;
    if (!z) return '—/—';
    return `${fmtInt(z.provareneCount)}/${fmtInt(z.totalCount)} ks`;
  };
  const fracRozPostaveny = (i: number): string => {
    const z = pts[i]?.metricsSnapshot?.rozvadec;
    if (!z) return '—/—';
    return `${fmtInt(z.postavenyCount ?? 0)}/${fmtInt(z.totalCount)} ks`;
  };
  const fracVr = (i: number): string => {
    const v = pts[i]?.metricsSnapshot?.vr;
    if (!v) return '—/—';
    return `${fmtInt(v.vrBranchCount)}/${fmtInt(v.totalObjektCount)} ks`;
  };
  const fracBabovka = (i: number): string => {
    const b = pts[i]?.metricsSnapshot?.babovka;
    if (!b) return '—/—';
    return `${fmtInt(b.builtCount)}/${fmtInt(b.totalCount)} ks`;
  };
  const fracZafouknuto = (i: number): string => {
    const z = pts[i]?.metricsSnapshot?.zafouknuto;
    if (!z) return '—/—';
    return `${fmtInt(z.finishedCount)}/${fmtInt(z.totalCount)} ks`;
  };
  const fracZafoukBabovka = (i: number): string => {
    const z = pts[i]?.metricsSnapshot?.zafoukBabovka;
    if (!z) return '—/—';
    const ks = `${fmtInt(z.finishedCount)}/${fmtInt(z.totalCount)} ks`;
    if (z.finishedHpCount == null && z.totalHpCount == null) return ks;
    return `${ks} · ${fmtInt(z.finishedHpCount ?? 0)}/${fmtInt(z.totalHpCount ?? 0)} HP`;
  };
  const fracZafoukPripojena = (i: number): string => {
    const z = pts[i]?.metricsSnapshot?.zafoukPripojenaBudova;
    if (!z) return '—/—';
    const ks = `${fmtInt(z.finishedCount)}/${fmtInt(z.totalCount)} ks`;
    if (z.finishedHpCount == null && z.totalHpCount == null) return ks;
    return `${ks} · ${fmtInt(z.finishedHpCount ?? 0)}/${fmtInt(z.totalHpCount ?? 0)} HP`;
  };
  /** Záfuk metráž v UI: zafoukáno jako jedno číslo (ne n/m). */
  const fracZafukOk = (i: number): string => {
    const z = pts[i]?.metricsSnapshot?.zafukOkDelka;
    if (!z) return '—';
    return `${fmtMPart(z.completedLengthM)} m`;
  };
  const fracZafukSvazek = (i: number): string => {
    const z = pts[i]?.metricsSnapshot?.zafukSvazekDelka;
    if (!z) return '—';
    return `${fmtMPart(z.completedLengthM)} m`;
  };
  const fracHp = (i: number): string => {
    const h = pts[i]?.metricsSnapshot?.hp;
    if (!h) return '—/—';
    const welded = h.weldedHpCount ?? h.connectedHpCount ?? 0;
    return `${fmtInt(welded)}/${fmtInt(h.totalHpCount)} HP`;
  };
  const fracAkvizice = (i: number): string => {
    const a = pts[i]?.metricsSnapshot?.akvizice;
    if (!a) return '—/—';
    return `${fmtInt(a.closedCount)}/${fmtInt(a.totalCount)} ks`;
  };
  const fracMatrixSpoj = (i: number): string => {
    const snapshot = pts[i]?.metricsSnapshot;
    const m = snapshot?.spojkoviste ?? snapshot?.matrixSpojkoviste;
    if (!m) return '—/—';
    return `${fmtInt(m.finishedCount)}/${fmtInt(m.totalCount)} ks`;
  };
  const fracZruseneBudovy = (i: number): string => {
    const z = pts[i]?.metricsSnapshot?.zruseneBudovy;
    if (!z) return '—/—';
    return `${fmtInt(z.cancelledCount)}/${fmtInt(z.totalCount)} ks`;
  };
  const fracDigitalniCesty = (i: number): string => {
    const d = pts[i]?.metricsSnapshot?.digitalniCesty;
    if (!d) return '—/—';
    return `${fmtInt(d.pathCount)}/${fmtInt(d.msoCount)} ks`;
  };
  const fracSvarObjekt = (i: number): string => {
    const s = pts[i]?.metricsSnapshot?.svarObjekt;
    if (!s) return '—/—';
    return `${fmtInt(s.finishedCount)}/${fmtInt(s.totalCount)} ks`;
  };
  const fracHotoveBudovy = (i: number): string => {
    const s = pts[i]?.metricsSnapshot?.hotoveBudovy;
    if (!s) return '—/—';
    return `${fmtInt(s.finishedCount)}/${fmtInt(s.totalCount)} ks`;
  };
  const fracRozZafouknuto = (i: number): string => {
    const z = pts[i]?.metricsSnapshot?.rozvadec;
    if (!z) return '—/—';
    return `${fmtInt(z.zafouknutoCount ?? 0)}/${fmtInt(z.totalCount)} ks`;
  };

  const labelTopSmall = {
    color: text,
    fontSize: 9,
    fontWeight: 500,
    lineHeight: 11,
    textBorderColor: dark ? '#171717' : '#ffffff',
    textBorderWidth: 2,
  };
  const ghostItemStyle = {
    borderColor: split,
    borderWidth: 1,
    color: dark ? 'rgba(148,163,184,0.1)' : 'rgba(100,116,139,0.16)',
    borderRadius: [4, 4, 0, 0] as [number, number, number, number],
  };

  const buildPercentStack = (
    color: string,
    getPair: (i: number) => { numerator: number; denominator: number },
  ): StackedPercentSeries => {
    const bottom: BarDatum[] = [];
    const top: BarDatum[] = [];
    for (let i = 0; i < pts.length; i++) {
      const { numerator, denominator } = getPair(i);
      const pct = kpiFractionPercent(numerator, denominator);
      const rem = Math.max(0, 100 - pct);
      const pctText = formatPctLabel(pct);
      bottom.push({
        value: pct,
        itemStyle: { color, borderRadius: [0, 0, 4, 4] as [number, number, number, number] },
        label:
          rem > 0
            ? { show: false }
            : { show: true, position: 'top' as const, distance: 5, formatter: pctText, ...labelTopSmall },
      });
      top.push({
        value: rem,
        itemStyle: ghostItemStyle,
        label:
          rem > 0
            ? { show: true, position: 'top' as const, distance: 4, formatter: pctText, ...labelTopSmall }
            : { show: false },
      });
    }
    return { bottom, top };
  };

  const routesR100 = buildPercentStack(MANAGER_KPI_SERIES_COLORS.routesR100, (i) => {
    const r = pts[i]?.metricsSnapshot?.routes?.r100;
    return { numerator: r?.excavatedLengthM ?? 0, denominator: r?.plannedLengthM ?? 0 };
  });
  const routesMet = buildPercentStack(MANAGER_KPI_SERIES_COLORS.routesMet, (i) => {
    const r = pts[i]?.metricsSnapshot?.routes?.met;
    return { numerator: r?.excavatedLengthM ?? 0, denominator: r?.plannedLengthM ?? 0 };
  });
  const routesUr = buildPercentStack(MANAGER_KPI_SERIES_COLORS.routesUr, (i) => {
    const r = pts[i]?.metricsSnapshot?.routes?.ur;
    return { numerator: r?.excavatedLengthM ?? 0, denominator: r?.plannedLengthM ?? 0 };
  });
  const drilled = buildPercentStack(MANAGER_KPI_SERIES_COLORS.objekt, (i) => {
    const o = pts[i]?.metricsSnapshot?.objekt;
    return { numerator: o?.drilledCount ?? 0, denominator: o?.totalCount ?? 0 };
  });
  const roz = buildPercentStack(MANAGER_KPI_SERIES_COLORS.rozvadec, (i) => {
    const z = pts[i]?.metricsSnapshot?.rozvadec;
    return { numerator: z?.provareneCount ?? 0, denominator: z?.totalCount ?? 0 };
  });
  const rozPostaveny = buildPercentStack(MANAGER_KPI_SERIES_COLORS.rozvadecPostaveny, (i) => {
    const z = pts[i]?.metricsSnapshot?.rozvadec;
    return { numerator: z?.postavenyCount ?? 0, denominator: z?.totalCount ?? 0 };
  });
  const vr = buildPercentStack(MANAGER_KPI_SERIES_COLORS.vr, (i) => {
    const v = pts[i]?.metricsSnapshot?.vr;
    return { numerator: v?.vrBranchCount ?? 0, denominator: v?.totalObjektCount ?? 0 };
  });
  const babovka = buildPercentStack(MANAGER_KPI_SERIES_COLORS.babovka, (i) => {
    const b = pts[i]?.metricsSnapshot?.babovka;
    return { numerator: b?.builtCount ?? 0, denominator: b?.totalCount ?? 0 };
  });
  const zafouknuto = buildPercentStack(MANAGER_KPI_SERIES_COLORS.zafouknuto, (i) => {
    const z = pts[i]?.metricsSnapshot?.zafouknuto;
    return { numerator: z?.finishedCount ?? 0, denominator: z?.totalCount ?? 0 };
  });
  const zafoukBabovka = buildPercentStack(MANAGER_KPI_SERIES_COLORS.zafoukBabovka, (i) => {
    const z = pts[i]?.metricsSnapshot?.zafoukBabovka;
    return { numerator: z?.finishedCount ?? 0, denominator: z?.totalCount ?? 0 };
  });
  const zafoukPripojena = buildPercentStack(MANAGER_KPI_SERIES_COLORS.zafoukPripojenaBudova, (i) => {
    const z = pts[i]?.metricsSnapshot?.zafoukPripojenaBudova;
    return { numerator: z?.finishedCount ?? 0, denominator: z?.totalCount ?? 0 };
  });
  const zafukOkDelka = buildPercentStack(MANAGER_KPI_SERIES_COLORS.zafukOkDelka, (i) => {
    const z = pts[i]?.metricsSnapshot?.zafukOkDelka;
    return { numerator: z?.completedLengthM ?? 0, denominator: z?.totalLengthM ?? 0 };
  });
  const zafukSvazekDelka = buildPercentStack(MANAGER_KPI_SERIES_COLORS.zafukSvazekDelka, (i) => {
    const z = pts[i]?.metricsSnapshot?.zafukSvazekDelka;
    return { numerator: z?.completedLengthM ?? 0, denominator: z?.totalLengthM ?? 0 };
  });
  const hpPocet = buildPercentStack(MANAGER_KPI_SERIES_COLORS.hpPocet, (i) => {
    const h = pts[i]?.metricsSnapshot?.hp;
    return {
      numerator: h?.weldedHpCount ?? h?.connectedHpCount ?? 0,
      denominator: h?.totalHpCount ?? 0,
    };
  });
  const akvizicePocet = buildPercentStack(MANAGER_KPI_SERIES_COLORS.akvizicePocet, (i) => {
    const a = pts[i]?.metricsSnapshot?.akvizice;
    return { numerator: a?.closedCount ?? 0, denominator: a?.totalCount ?? 0 };
  });
  const matrixSpoj = buildPercentStack(MANAGER_KPI_SERIES_COLORS.matrixSpoj, (i) => {
    const snapshot = pts[i]?.metricsSnapshot;
    const m = snapshot?.spojkoviste ?? snapshot?.matrixSpojkoviste;
    return { numerator: m?.finishedCount ?? 0, denominator: m?.totalCount ?? 0 };
  });
  const zruseneBudovy = buildPercentStack(MANAGER_KPI_SERIES_COLORS.zruseneBudovy, (i) => {
    const z = pts[i]?.metricsSnapshot?.zruseneBudovy;
    return { numerator: z?.cancelledCount ?? 0, denominator: z?.totalCount ?? 0 };
  });
  const digitalniCesty = buildPercentStack(MANAGER_KPI_SERIES_COLORS.digitalniCesty, (i) => {
    const d = pts[i]?.metricsSnapshot?.digitalniCesty;
    return { numerator: d?.pathCount ?? 0, denominator: d?.msoCount ?? 0 };
  });
  const svarObjekt = buildPercentStack(MANAGER_KPI_SERIES_COLORS.svarObjekt, (i) => {
    const s = pts[i]?.metricsSnapshot?.svarObjekt;
    return { numerator: s?.finishedCount ?? 0, denominator: s?.totalCount ?? 0 };
  });
  const hotoveBudovy = buildPercentStack(MANAGER_KPI_SERIES_COLORS.hotoveBudovy, (i) => {
    const s = pts[i]?.metricsSnapshot?.hotoveBudovy;
    return { numerator: s?.finishedCount ?? 0, denominator: s?.totalCount ?? 0 };
  });
  const rozZafouknuto = buildPercentStack(MANAGER_KPI_SERIES_COLORS.rozvadecZafouknuto, (i) => {
    const z = pts[i]?.metricsSnapshot?.rozvadec;
    return { numerator: z?.zafouknutoCount ?? 0, denominator: z?.totalCount ?? 0 };
  });

  const absoluteBySeriesName: Record<string, (i: number) => string> = {
    [MANAGER_KPI_SERIES_NAMES.routesR100]: fracRoutesR100,
    [MANAGER_KPI_SERIES_NAMES.routesMet]: fracRoutesMet,
    [MANAGER_KPI_SERIES_NAMES.routesUr]: fracRoutesUr,
    [MANAGER_KPI_SERIES_NAMES.objekt]: fracDrilled,
    [MANAGER_KPI_SERIES_NAMES.rozvadec]: fracRoz,
    [MANAGER_KPI_SERIES_NAMES.rozvadecPostaveny]: fracRozPostaveny,
    [MANAGER_KPI_SERIES_NAMES.rozvadecZafouknuto]: fracRozZafouknuto,
    [MANAGER_KPI_SERIES_NAMES.vr]: fracVr,
    [MANAGER_KPI_SERIES_NAMES.babovka]: fracBabovka,
    [MANAGER_KPI_SERIES_NAMES.zafouknuto]: fracZafouknuto,
    [MANAGER_KPI_SERIES_NAMES.zafoukBabovka]: fracZafoukBabovka,
    [MANAGER_KPI_SERIES_NAMES.zafoukPripojenaBudova]: fracZafoukPripojena,
    [MANAGER_KPI_SERIES_NAMES.zafukOkDelka]: fracZafukOk,
    [MANAGER_KPI_SERIES_NAMES.zafukSvazekDelka]: fracZafukSvazek,
    [MANAGER_KPI_SERIES_NAMES.hpPocet]: fracHp,
    [MANAGER_KPI_SERIES_NAMES.akvizicePocet]: fracAkvizice,
    [MANAGER_KPI_SERIES_NAMES.matrixSpoj]: fracMatrixSpoj,
    [MANAGER_KPI_SERIES_NAMES.zruseneBudovy]: fracZruseneBudovy,
    [MANAGER_KPI_SERIES_NAMES.digitalniCesty]: fracDigitalniCesty,
    [MANAGER_KPI_SERIES_NAMES.svarObjekt]: fracSvarObjekt,
    [MANAGER_KPI_SERIES_NAMES.hotoveBudovy]: fracHotoveBudovy,
  };

  const pctBySeriesName: Record<string, BarDatum[]> = {
    [MANAGER_KPI_SERIES_NAMES.routesR100]: routesR100.bottom,
    [MANAGER_KPI_SERIES_NAMES.routesMet]: routesMet.bottom,
    [MANAGER_KPI_SERIES_NAMES.routesUr]: routesUr.bottom,
    [MANAGER_KPI_SERIES_NAMES.objekt]: drilled.bottom,
    [MANAGER_KPI_SERIES_NAMES.rozvadec]: roz.bottom,
    [MANAGER_KPI_SERIES_NAMES.rozvadecPostaveny]: rozPostaveny.bottom,
    [MANAGER_KPI_SERIES_NAMES.rozvadecZafouknuto]: rozZafouknuto.bottom,
    [MANAGER_KPI_SERIES_NAMES.vr]: vr.bottom,
    [MANAGER_KPI_SERIES_NAMES.babovka]: babovka.bottom,
    [MANAGER_KPI_SERIES_NAMES.zafouknuto]: zafouknuto.bottom,
    [MANAGER_KPI_SERIES_NAMES.zafoukBabovka]: zafoukBabovka.bottom,
    [MANAGER_KPI_SERIES_NAMES.zafoukPripojenaBudova]: zafoukPripojena.bottom,
    [MANAGER_KPI_SERIES_NAMES.zafukOkDelka]: zafukOkDelka.bottom,
    [MANAGER_KPI_SERIES_NAMES.zafukSvazekDelka]: zafukSvazekDelka.bottom,
    [MANAGER_KPI_SERIES_NAMES.hpPocet]: hpPocet.bottom,
    [MANAGER_KPI_SERIES_NAMES.akvizicePocet]: akvizicePocet.bottom,
    [MANAGER_KPI_SERIES_NAMES.matrixSpoj]: matrixSpoj.bottom,
    [MANAGER_KPI_SERIES_NAMES.zruseneBudovy]: zruseneBudovy.bottom,
    [MANAGER_KPI_SERIES_NAMES.digitalniCesty]: digitalniCesty.bottom,
    [MANAGER_KPI_SERIES_NAMES.svarObjekt]: svarObjekt.bottom,
    [MANAGER_KPI_SERIES_NAMES.hotoveBudovy]: hotoveBudovy.bottom,
  };

  const kpiBarLabel = (seriesName: string, dataIndex: number): string => {
    const absFn = absoluteBySeriesName[seriesName];
    const bottom = pctBySeriesName[seriesName];
    if (!absFn || !bottom) return '';
    const pct = Number(bottom[dataIndex]?.value) || 0;
    return `${formatPctLabel(pct)} (${absFn(dataIndex)})`;
  };

  return {
    routesR100BottomData: routesR100.bottom,
    routesR100TopData: routesR100.top,
    routesMetBottomData: routesMet.bottom,
    routesMetTopData: routesMet.top,
    routesUrBottomData: routesUr.bottom,
    routesUrTopData: routesUr.top,
    drilledBottomData: drilled.bottom,
    drilledTopData: drilled.top,
    rozBottomData: roz.bottom,
    rozTopData: roz.top,
    rozPostavenyBottomData: rozPostaveny.bottom,
    rozPostavenyTopData: rozPostaveny.top,
    vrBottomData: vr.bottom,
    vrTopData: vr.top,
    babovkaBottomData: babovka.bottom,
    babovkaTopData: babovka.top,
    zafouknutoBottomData: zafouknuto.bottom,
    zafouknutoTopData: zafouknuto.top,
    zafoukBabovkaBottomData: zafoukBabovka.bottom,
    zafoukBabovkaTopData: zafoukBabovka.top,
    zafoukPripojenaBottomData: zafoukPripojena.bottom,
    zafoukPripojenaTopData: zafoukPripojena.top,
    zafukOkDelkaBottomData: zafukOkDelka.bottom,
    zafukOkDelkaTopData: zafukOkDelka.top,
    zafukSvazekDelkaBottomData: zafukSvazekDelka.bottom,
    zafukSvazekDelkaTopData: zafukSvazekDelka.top,
    hpPocetBottomData: hpPocet.bottom,
    hpPocetTopData: hpPocet.top,
    akvizicePocetBottomData: akvizicePocet.bottom,
    akvizicePocetTopData: akvizicePocet.top,
    matrixSpojBottomData: matrixSpoj.bottom,
    matrixSpojTopData: matrixSpoj.top,
    zruseneBudovyBottomData: zruseneBudovy.bottom,
    zruseneBudovyTopData: zruseneBudovy.top,
    digitalniCestyBottomData: digitalniCesty.bottom,
    digitalniCestyTopData: digitalniCesty.top,
    svarObjektBottomData: svarObjekt.bottom,
    svarObjektTopData: svarObjekt.top,
    hotoveBudovyBottomData: hotoveBudovy.bottom,
    hotoveBudovyTopData: hotoveBudovy.top,
    rozZafouknutoBottomData: rozZafouknuto.bottom,
    rozZafouknutoTopData: rozZafouknuto.top,
    kpiBarLabel,
  };
}

function percentBarSeries(args: {
  id: string;
  name: string;
  stack: string;
  color: string;
  bottom: BarDatum[];
  top: BarDatum[];
}): BarSeriesOption[] {
  return [
    {
      id: `${args.id}_bottom`,
      name: args.name,
      type: 'bar',
      stack: args.stack,
      yAxisIndex: 0,
      barMaxWidth: 18,
      barCategoryGap: '48%',
      barGap: '42%',
      itemStyle: { color: args.color },
      data: args.bottom,
    },
    {
      id: `${args.id}_cap`,
      name: `_${args.id}_cap`,
      type: 'bar',
      stack: args.stack,
      yAxisIndex: 0,
      barMaxWidth: 18,
      silent: true,
      tooltip: { show: false },
      data: args.top,
    },
  ];
}

/** Mapování karty → popisek řady (pro filtrování grafu podle kategorie). */
const KPI_CARD_TO_SERIES_NAME: Partial<Record<ManagerKpiCardId, string>> = {
  routesR100: MANAGER_KPI_SERIES_NAMES.routesR100,
  routesMet: MANAGER_KPI_SERIES_NAMES.routesMet,
  routesUr: MANAGER_KPI_SERIES_NAMES.routesUr,
  digitalniCesty: MANAGER_KPI_SERIES_NAMES.digitalniCesty,
  vr: MANAGER_KPI_SERIES_NAMES.vr,
  matrixSpoj: MANAGER_KPI_SERIES_NAMES.matrixSpoj,
  zafoukBabovka: MANAGER_KPI_SERIES_NAMES.zafoukBabovka,
  zafoukPripojenaBudova: MANAGER_KPI_SERIES_NAMES.zafoukPripojenaBudova,
  zafukOkDelka: MANAGER_KPI_SERIES_NAMES.zafukOkDelka,
  zafukSvazekDelka: MANAGER_KPI_SERIES_NAMES.zafukSvazekDelka,
  hpPocet: MANAGER_KPI_SERIES_NAMES.hpPocet,
  akvizicePocet: MANAGER_KPI_SERIES_NAMES.akvizicePocet,
  svarObjekt: MANAGER_KPI_SERIES_NAMES.svarObjekt,
  hotoveBudovy: MANAGER_KPI_SERIES_NAMES.hotoveBudovy,
  objekt: MANAGER_KPI_SERIES_NAMES.objekt,
  babovka: MANAGER_KPI_SERIES_NAMES.babovka,
  zruseneBudovy: MANAGER_KPI_SERIES_NAMES.zruseneBudovy,
  rozvadecPostaveny: MANAGER_KPI_SERIES_NAMES.rozvadecPostaveny,
  rozvadecZafouknuto: MANAGER_KPI_SERIES_NAMES.rozvadecZafouknuto,
  rozvadec: MANAGER_KPI_SERIES_NAMES.rozvadec,
  zafouknuto: MANAGER_KPI_SERIES_NAMES.zafouknuto,
};

/** Popisek řady grafu → KPI karta (klik → Fronta práce). */
export function managerKpiCardIdFromSeriesName(seriesName: string): ManagerKpiCardId | null {
  const name = (seriesName ?? '').trim();
  if (!name) return null;
  for (const [cardId, label] of Object.entries(KPI_CARD_TO_SERIES_NAME) as Array<
    [ManagerKpiCardId, string]
  >) {
    if (label === name) return cardId;
  }
  return null;
}

function filterSeriesByCardIds<T extends { name: string }>(
  defs: T[],
  seriesCardIds: readonly ManagerKpiCardId[] | null | undefined,
): T[] {
  if (!seriesCardIds?.length) return defs;
  const allowed = new Set(
    seriesCardIds
      .map((id) => KPI_CARD_TO_SERIES_NAME[id])
      .filter((name): name is string => !!name),
  );
  if (allowed.size === 0) return defs;
  return defs.filter((d) => allowed.has(d.name));
}

export function buildManagerKpiChartOption(args: {
  pts: ManagerDashboardTimeseriesPoint[];
  bucket: ManagerDashboardTimeseriesResult['bucket'];
  dark: boolean;
  escapeHtml?: (s: string) => string;
  /** Omezí řady na karty kategorie (detail hubu). Bez filtru = všechny. */
  seriesCardIds?: readonly ManagerKpiCardId[] | null;
  reduceMotion?: boolean;
}): EChartsOption {
  const escapeHtml = args.escapeHtml ?? escapeHtmlForChartTooltip;
  const pts = args.pts;
  const bucket = args.bucket;
  const dark = args.dark;
  const categories = pts.map((p) => formatTimeseriesBucketLabel(p.bucket, bucket));
  const text = dark ? '#f5f5f5' : '#262626';
  const split = dark ? '#404040' : '#e5e5e5';
  const chipBg = dark ? 'rgba(38,38,38,0.94)' : 'rgba(250,250,250,0.96)';
  const chipBorder = dark ? '#525252' : '#d4d4d4';
  const chipPad = [3, 6] as [number, number];
  const axisLabelChip = {
    color: text,
    backgroundColor: chipBg,
    borderColor: chipBorder,
    borderWidth: 1,
    borderRadius: 4,
    padding: chipPad,
    fontSize: 11,
  };
  const yPercentLabel = {
    ...axisLabelChip,
    formatter: (v: number) => `${v}%`,
  };
  const nameChip = {
    backgroundColor: chipBg,
    borderColor: chipBorder,
    borderWidth: 1,
    borderRadius: 4,
    padding: [2, 8] as [number, number],
    fontSize: 11,
  };
  const xAxisLabelShared = {
    ...axisLabelChip,
    rotate: 22,
    interval: 0,
    margin: 14,
    hideOverlap: false,
  };
  const {
    routesR100BottomData,
    routesR100TopData,
    routesMetBottomData,
    routesMetTopData,
    routesUrBottomData,
    routesUrTopData,
    drilledBottomData,
    drilledTopData,
    rozBottomData,
    rozTopData,
    rozPostavenyBottomData,
    rozPostavenyTopData,
    vrBottomData,
    vrTopData,
    babovkaBottomData,
    babovkaTopData,
    zafouknutoBottomData,
    zafouknutoTopData,
    zafoukBabovkaBottomData,
    zafoukBabovkaTopData,
    zafoukPripojenaBottomData,
    zafoukPripojenaTopData,
    zafukOkDelkaBottomData,
    zafukOkDelkaTopData,
    zafukSvazekDelkaBottomData,
    zafukSvazekDelkaTopData,
    hpPocetBottomData,
    hpPocetTopData,
    akvizicePocetBottomData,
    akvizicePocetTopData,
    matrixSpojBottomData,
    matrixSpojTopData,
    zruseneBudovyBottomData,
    zruseneBudovyTopData,
    digitalniCestyBottomData,
    digitalniCestyTopData,
    svarObjektBottomData,
    svarObjektTopData,
    hotoveBudovyBottomData,
    hotoveBudovyTopData,
    rozZafouknutoBottomData,
    rozZafouknutoTopData,
    kpiBarLabel,
  } = buildKpiStackedBarSeriesData(pts, text, dark, split);

  const seriesDefs: Array<{
    id: string;
    name: string;
    stack: string;
    color: string;
    bottom: BarDatum[];
    top: BarDatum[];
  }> = [
    {
      id: 'kpi_routes_r100',
      name: MANAGER_KPI_SERIES_NAMES.routesR100,
      stack: 'kpi_routes_r100',
      color: MANAGER_KPI_SERIES_COLORS.routesR100,
      bottom: routesR100BottomData,
      top: routesR100TopData,
    },
    {
      id: 'kpi_routes_met',
      name: MANAGER_KPI_SERIES_NAMES.routesMet,
      stack: 'kpi_routes_met',
      color: MANAGER_KPI_SERIES_COLORS.routesMet,
      bottom: routesMetBottomData,
      top: routesMetTopData,
    },
    {
      id: 'kpi_routes_ur',
      name: MANAGER_KPI_SERIES_NAMES.routesUr,
      stack: 'kpi_routes_ur',
      color: MANAGER_KPI_SERIES_COLORS.routesUr,
      bottom: routesUrBottomData,
      top: routesUrTopData,
    },
    {
      id: 'kpi_digitalni_cesty',
      name: MANAGER_KPI_SERIES_NAMES.digitalniCesty,
      stack: 'kpi_digitalni_cesty',
      color: MANAGER_KPI_SERIES_COLORS.digitalniCesty,
      bottom: digitalniCestyBottomData,
      top: digitalniCestyTopData,
    },
    {
      id: 'kpi_vr',
      name: MANAGER_KPI_SERIES_NAMES.vr,
      stack: 'kpi_vr',
      color: MANAGER_KPI_SERIES_COLORS.vr,
      bottom: vrBottomData,
      top: vrTopData,
    },
    {
      id: 'kpi_matrix_spoj',
      name: MANAGER_KPI_SERIES_NAMES.matrixSpoj,
      stack: 'kpi_matrix_spoj',
      color: MANAGER_KPI_SERIES_COLORS.matrixSpoj,
      bottom: matrixSpojBottomData,
      top: matrixSpojTopData,
    },
    {
      id: 'kpi_zafouk_babovka',
      name: MANAGER_KPI_SERIES_NAMES.zafoukBabovka,
      stack: 'kpi_zafouk_babovka',
      color: MANAGER_KPI_SERIES_COLORS.zafoukBabovka,
      bottom: zafoukBabovkaBottomData,
      top: zafoukBabovkaTopData,
    },
    {
      id: 'kpi_zafouk_pripojena',
      name: MANAGER_KPI_SERIES_NAMES.zafoukPripojenaBudova,
      stack: 'kpi_zafouk_pripojena',
      color: MANAGER_KPI_SERIES_COLORS.zafoukPripojenaBudova,
      bottom: zafoukPripojenaBottomData,
      top: zafoukPripojenaTopData,
    },
    {
      id: 'kpi_zafuk_ok',
      name: MANAGER_KPI_SERIES_NAMES.zafukOkDelka,
      stack: 'kpi_zafuk_ok',
      color: MANAGER_KPI_SERIES_COLORS.zafukOkDelka,
      bottom: zafukOkDelkaBottomData,
      top: zafukOkDelkaTopData,
    },
    {
      id: 'kpi_zafuk_svazek',
      name: MANAGER_KPI_SERIES_NAMES.zafukSvazekDelka,
      stack: 'kpi_zafuk_svazek',
      color: MANAGER_KPI_SERIES_COLORS.zafukSvazekDelka,
      bottom: zafukSvazekDelkaBottomData,
      top: zafukSvazekDelkaTopData,
    },
    {
      id: 'kpi_hp',
      name: MANAGER_KPI_SERIES_NAMES.hpPocet,
      stack: 'kpi_hp',
      color: MANAGER_KPI_SERIES_COLORS.hpPocet,
      bottom: hpPocetBottomData,
      top: hpPocetTopData,
    },
    {
      id: 'kpi_akvizice',
      name: MANAGER_KPI_SERIES_NAMES.akvizicePocet,
      stack: 'kpi_akvizice',
      color: MANAGER_KPI_SERIES_COLORS.akvizicePocet,
      bottom: akvizicePocetBottomData,
      top: akvizicePocetTopData,
    },
    {
      id: 'kpi_svar_objekt',
      name: MANAGER_KPI_SERIES_NAMES.svarObjekt,
      stack: 'kpi_svar_objekt',
      color: MANAGER_KPI_SERIES_COLORS.svarObjekt,
      bottom: svarObjektBottomData,
      top: svarObjektTopData,
    },
    {
      id: 'kpi_hotove_budovy',
      name: MANAGER_KPI_SERIES_NAMES.hotoveBudovy,
      stack: 'kpi_hotove_budovy',
      color: MANAGER_KPI_SERIES_COLORS.hotoveBudovy,
      bottom: hotoveBudovyBottomData,
      top: hotoveBudovyTopData,
    },
    {
      id: 'kpi_drilled',
      name: MANAGER_KPI_SERIES_NAMES.objekt,
      stack: 'kpi_drilled',
      color: MANAGER_KPI_SERIES_COLORS.objekt,
      bottom: drilledBottomData,
      top: drilledTopData,
    },
    {
      id: 'kpi_babovka',
      name: MANAGER_KPI_SERIES_NAMES.babovka,
      stack: 'kpi_babovka',
      color: MANAGER_KPI_SERIES_COLORS.babovka,
      bottom: babovkaBottomData,
      top: babovkaTopData,
    },
    {
      id: 'kpi_zrusene_budovy',
      name: MANAGER_KPI_SERIES_NAMES.zruseneBudovy,
      stack: 'kpi_zrusene_budovy',
      color: MANAGER_KPI_SERIES_COLORS.zruseneBudovy,
      bottom: zruseneBudovyBottomData,
      top: zruseneBudovyTopData,
    },
    {
      id: 'kpi_roz_postaveny',
      name: MANAGER_KPI_SERIES_NAMES.rozvadecPostaveny,
      stack: 'kpi_roz_postaveny',
      color: MANAGER_KPI_SERIES_COLORS.rozvadecPostaveny,
      bottom: rozPostavenyBottomData,
      top: rozPostavenyTopData,
    },
    {
      id: 'kpi_roz_zafouknuto',
      name: MANAGER_KPI_SERIES_NAMES.rozvadecZafouknuto,
      stack: 'kpi_roz_zafouknuto',
      color: MANAGER_KPI_SERIES_COLORS.rozvadecZafouknuto,
      bottom: rozZafouknutoBottomData,
      top: rozZafouknutoTopData,
    },
    {
      id: 'kpi_roz',
      name: MANAGER_KPI_SERIES_NAMES.rozvadec,
      stack: 'kpi_roz',
      color: MANAGER_KPI_SERIES_COLORS.rozvadec,
      bottom: rozBottomData,
      top: rozTopData,
    },
  ];

  const visibleSeriesDefs = filterSeriesByCardIds(seriesDefs, args.seriesCardIds);

  return {
    textStyle: { color: text },
    labelLayout: { hideOverlap: true },
    ...echartsEntranceMotion(args.reduceMotion),
    universalTransition: {
      enabled: true,
      delay: (index: number, count: number) => Math.round((180 * index) / Math.max(count, 1)),
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: dark ? 'rgba(23,23,23,0.96)' : 'rgba(255,255,255,0.98)',
      borderColor: chipBorder,
      borderWidth: 1,
      textStyle: { color: text },
      formatter: (params: unknown) => {
        const arr = params as Array<{
          axisValue: string;
          marker: string;
          seriesName: string;
          value: number;
          dataIndex?: number;
        }>;
        if (!Array.isArray(arr) || arr.length === 0) return '';
        const di = arr[0].dataIndex ?? 0;
        const title = `<div style="font-weight:600;margin-bottom:6px;padding:4px 8px;border-radius:4px;background:${chipBg};border:1px solid ${chipBorder}">${escapeHtml(
          arr[0].axisValue,
        )}</div>`;
        const rows = arr
          .filter((p) => !p.seriesName.startsWith('_'))
          .map((p) => {
            const detail = kpiBarLabel(p.seriesName, p.dataIndex ?? di);
            const shown = detail || `${formatPctLabel(Number(p.value) || 0)}`;
            return `${p.marker} <span style="margin-left:4px">${escapeHtml(p.seriesName)}</span>: <strong>${escapeHtml(
              shown,
            )}</strong>`;
          })
          .join('<br/>');
        return title + rows;
      },
    },
    legend: {
      type: 'scroll',
      data: visibleSeriesDefs.map((s) => ({ name: s.name, itemStyle: { color: s.color } })),
      textStyle: { color: text },
      backgroundColor: chipBg,
      borderColor: chipBorder,
      borderWidth: 1,
      borderRadius: 8,
      padding: [8, 12],
      top: 2,
    },
    grid: { left: '4%', right: '3%', top: '14%', bottom: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: xAxisLabelShared,
      axisLine: { lineStyle: { color: split } },
      axisTick: { alignWithLabel: true },
    },
    yAxis: {
      type: 'value',
      name: '%',
      min: 0,
      max: 100,
      interval: 25,
      axisLabel: yPercentLabel,
      axisLine: { show: true, lineStyle: { color: dark ? '#a3a3a3' : '#737373' } },
      nameTextStyle: { ...nameChip, color: text },
      splitLine: { lineStyle: { color: split } },
    },
    series: visibleSeriesDefs.flatMap((s) => percentBarSeries(s)),
  };
}

export type ManagerKpiChartVariant = 'bar' | 'line';

function percentValuesFromBarData(data: BarDatum[]): number[] {
  return data.map((d) => Number(d.value) || 0);
}

/** Růst v čase v detailu kategorie — stejné KPI % , čárový graf (bez skládaných sloupců). */
export function buildManagerKpiLineChartOption(args: {
  pts: ManagerDashboardTimeseriesPoint[];
  bucket: ManagerDashboardTimeseriesResult['bucket'];
  dark: boolean;
  escapeHtml?: (s: string) => string;
  /** Omezí řady na karty kategorie (detail hubu). Bez filtru = všechny. */
  seriesCardIds?: readonly ManagerKpiCardId[] | null;
  reduceMotion?: boolean;
}): EChartsOption {
  const escapeHtml = args.escapeHtml ?? escapeHtmlForChartTooltip;
  const pts = args.pts;
  const bucket = args.bucket;
  const dark = args.dark;
  const categories = pts.map((p) => formatTimeseriesBucketLabel(p.bucket, bucket));
  const text = dark ? '#f5f5f5' : '#262626';
  const split = dark ? '#404040' : '#e5e5e5';
  const chipBg = dark ? 'rgba(38,38,38,0.94)' : 'rgba(250,250,250,0.96)';
  const chipBorder = dark ? '#525252' : '#d4d4d4';
  const chipPad = [3, 6] as [number, number];
  const axisLabelChip = {
    color: text,
    backgroundColor: chipBg,
    borderColor: chipBorder,
    borderWidth: 1,
    borderRadius: 4,
    padding: chipPad,
    fontSize: 11,
  };
  const yPercentLabel = {
    ...axisLabelChip,
    formatter: (v: number) => `${v}%`,
  };
  const nameChip = {
    backgroundColor: chipBg,
    borderColor: chipBorder,
    borderWidth: 1,
    borderRadius: 4,
    padding: [2, 8] as [number, number],
    fontSize: 11,
  };
  const xAxisLabelShared = {
    ...axisLabelChip,
    rotate: 22,
    interval: 0,
    margin: 14,
    hideOverlap: false,
  };

  const barBundle = buildKpiStackedBarSeriesData(pts, text, dark, split);
  const { kpiBarLabel } = barBundle;

  type LineDef = { id: string; name: string; color: string; bottom: BarDatum[] };
  const lineDefs: LineDef[] = [
    { id: 'kpi_routes_r100', name: MANAGER_KPI_SERIES_NAMES.routesR100, color: MANAGER_KPI_SERIES_COLORS.routesR100, bottom: barBundle.routesR100BottomData },
    { id: 'kpi_routes_met', name: MANAGER_KPI_SERIES_NAMES.routesMet, color: MANAGER_KPI_SERIES_COLORS.routesMet, bottom: barBundle.routesMetBottomData },
    { id: 'kpi_routes_ur', name: MANAGER_KPI_SERIES_NAMES.routesUr, color: MANAGER_KPI_SERIES_COLORS.routesUr, bottom: barBundle.routesUrBottomData },
    { id: 'kpi_digitalni_cesty', name: MANAGER_KPI_SERIES_NAMES.digitalniCesty, color: MANAGER_KPI_SERIES_COLORS.digitalniCesty, bottom: barBundle.digitalniCestyBottomData },
    { id: 'kpi_vr', name: MANAGER_KPI_SERIES_NAMES.vr, color: MANAGER_KPI_SERIES_COLORS.vr, bottom: barBundle.vrBottomData },
    { id: 'kpi_matrix_spoj', name: MANAGER_KPI_SERIES_NAMES.matrixSpoj, color: MANAGER_KPI_SERIES_COLORS.matrixSpoj, bottom: barBundle.matrixSpojBottomData },
    { id: 'kpi_zafouk_babovka', name: MANAGER_KPI_SERIES_NAMES.zafoukBabovka, color: MANAGER_KPI_SERIES_COLORS.zafoukBabovka, bottom: barBundle.zafoukBabovkaBottomData },
    { id: 'kpi_zafouk_pripojena', name: MANAGER_KPI_SERIES_NAMES.zafoukPripojenaBudova, color: MANAGER_KPI_SERIES_COLORS.zafoukPripojenaBudova, bottom: barBundle.zafoukPripojenaBottomData },
    { id: 'kpi_zafuk_ok', name: MANAGER_KPI_SERIES_NAMES.zafukOkDelka, color: MANAGER_KPI_SERIES_COLORS.zafukOkDelka, bottom: barBundle.zafukOkDelkaBottomData },
    { id: 'kpi_zafuk_svazek', name: MANAGER_KPI_SERIES_NAMES.zafukSvazekDelka, color: MANAGER_KPI_SERIES_COLORS.zafukSvazekDelka, bottom: barBundle.zafukSvazekDelkaBottomData },
    { id: 'kpi_hp', name: MANAGER_KPI_SERIES_NAMES.hpPocet, color: MANAGER_KPI_SERIES_COLORS.hpPocet, bottom: barBundle.hpPocetBottomData },
    { id: 'kpi_akvizice', name: MANAGER_KPI_SERIES_NAMES.akvizicePocet, color: MANAGER_KPI_SERIES_COLORS.akvizicePocet, bottom: barBundle.akvizicePocetBottomData },
    { id: 'kpi_svar_objekt', name: MANAGER_KPI_SERIES_NAMES.svarObjekt, color: MANAGER_KPI_SERIES_COLORS.svarObjekt, bottom: barBundle.svarObjektBottomData },
    { id: 'kpi_hotove_budovy', name: MANAGER_KPI_SERIES_NAMES.hotoveBudovy, color: MANAGER_KPI_SERIES_COLORS.hotoveBudovy, bottom: barBundle.hotoveBudovyBottomData },
    { id: 'kpi_drilled', name: MANAGER_KPI_SERIES_NAMES.objekt, color: MANAGER_KPI_SERIES_COLORS.objekt, bottom: barBundle.drilledBottomData },
    { id: 'kpi_babovka', name: MANAGER_KPI_SERIES_NAMES.babovka, color: MANAGER_KPI_SERIES_COLORS.babovka, bottom: barBundle.babovkaBottomData },
    { id: 'kpi_zrusene_budovy', name: MANAGER_KPI_SERIES_NAMES.zruseneBudovy, color: MANAGER_KPI_SERIES_COLORS.zruseneBudovy, bottom: barBundle.zruseneBudovyBottomData },
    { id: 'kpi_roz_postaveny', name: MANAGER_KPI_SERIES_NAMES.rozvadecPostaveny, color: MANAGER_KPI_SERIES_COLORS.rozvadecPostaveny, bottom: barBundle.rozPostavenyBottomData },
    { id: 'kpi_roz_zafouknuto', name: MANAGER_KPI_SERIES_NAMES.rozvadecZafouknuto, color: MANAGER_KPI_SERIES_COLORS.rozvadecZafouknuto, bottom: barBundle.rozZafouknutoBottomData },
    { id: 'kpi_roz', name: MANAGER_KPI_SERIES_NAMES.rozvadec, color: MANAGER_KPI_SERIES_COLORS.rozvadec, bottom: barBundle.rozBottomData },
  ];

  const visibleLineDefs = filterSeriesByCardIds(lineDefs, args.seriesCardIds);

  const lineSeries: LineSeriesOption[] = visibleLineDefs.map((def) => ({
    id: def.id,
    name: def.name,
    type: 'line',
    smooth: 0.25,
    showSymbol: pts.length <= 24,
    symbolSize: 6,
    lineStyle: { width: 2, color: def.color },
    itemStyle: { color: def.color },
    data: percentValuesFromBarData(def.bottom),
  }));

  return {
    textStyle: { color: text },
    ...echartsEntranceMotion(args.reduceMotion),
    tooltip: {
      trigger: 'axis',
      backgroundColor: dark ? 'rgba(23,23,23,0.96)' : 'rgba(255,255,255,0.98)',
      borderColor: chipBorder,
      borderWidth: 1,
      textStyle: { color: text },
      formatter: (params: unknown) => {
        const arr = params as Array<{
          axisValue: string;
          marker: string;
          seriesName: string;
          value: number;
          dataIndex?: number;
        }>;
        if (!Array.isArray(arr) || arr.length === 0) return '';
        const di = arr[0].dataIndex ?? 0;
        const title = `<div style="font-weight:600;margin-bottom:6px;padding:4px 8px;border-radius:4px;background:${chipBg};border:1px solid ${chipBorder}">${escapeHtml(
          arr[0].axisValue,
        )}</div>`;
        const rows = arr
          .map((p) => {
            const detail = kpiBarLabel(p.seriesName, p.dataIndex ?? di);
            const shown = detail || `${formatPctLabel(Number(p.value) || 0)}`;
            return `${p.marker} <span style="margin-left:4px">${escapeHtml(p.seriesName)}</span>: <strong>${escapeHtml(
              shown,
            )}</strong>`;
          })
          .join('<br/>');
        return title + rows;
      },
    },
    legend: {
      type: 'scroll',
      data: visibleLineDefs.map((s) => ({ name: s.name, itemStyle: { color: s.color } })),
      textStyle: { color: text },
      backgroundColor: chipBg,
      borderColor: chipBorder,
      borderWidth: 1,
      borderRadius: 8,
      padding: [8, 12],
      top: 2,
    },
    grid: { left: '4%', right: '3%', top: '14%', bottom: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: xAxisLabelShared,
      axisLine: { lineStyle: { color: split } },
      axisTick: { alignWithLabel: true },
    },
    yAxis: {
      type: 'value',
      name: '%',
      min: 0,
      max: 100,
      interval: 25,
      axisLabel: yPercentLabel,
      axisLine: { show: true, lineStyle: { color: dark ? '#a3a3a3' : '#737373' } },
      nameTextStyle: { ...nameChip, color: text },
      splitLine: { lineStyle: { color: split } },
    },
    series: lineSeries,
  };
}
